/**
 * Detection of "waiting on paperwork" wording in event note text, used to
 * auto-populate the Audit modal's Waiting Paperwork time.
 *
 * The pattern captures phrasings indicating the only remaining reason an event
 * is not closed (aircraft not returned to service / not "green") is completing
 * the return-to-service paperwork. It is sourced from real carrier update wording
 * across the monitored feeds (21 Air SOC, ABX/ATI ATSG "Aircraft Update
 * Notification", ASL AMOS, Hawaiian/Alaska Smartsheet MOC, Sun Country SOC) —
 * e.g. ATSG's "Mx working on paperwork." — and grouped by the document
 * (paperwork / log page(s) / log book / "LP" / signature / release) and the
 * state verb (waiting / awaiting / pending / working / finalizing / completing /
 * finishing / closing out / wrapping up). Alternatives are intentionally
 * anchored (never a bare "paperwork") to avoid false positives on notes that
 * merely mention paperwork in passing or report it already received.
 */
const PAPERWORK_WAIT_ALTERNATIVES: readonly string[] = [
  // Paperwork
  'down to (?:the )?paperwork',
  'waiting (?:on|for) (?:the )?paperwork',
  'awaiting (?:the )?paperwork',
  'waiting paperwork',
  'paperwork (?:in work|pending|remaining)',
  'pending paperwork',
  'remaining paperwork',
  'paperwork only',
  'only paperwork (?:remaining|left)',
  'just (?:need|needs|awaiting) paperwork',
  // Log page(s)
  'awaiting log ?pages?',
  'log ?pages? (?:in work|pending|remaining)',
  'waiting (?:on|for) (?:the )?log ?pages?',
  'down to (?:the )?log ?pages?',
  'pending log ?pages?',
  // "LP" (log-page abbreviation)
  'waiting (?:on|for) lp',
  'down to lp',
  'lp (?:in work|pending)',
  // Log book
  'awaiting log ?book',
  'log ?book (?:in work|pending)',
  'waiting (?:on|for) (?:the )?log ?book',
  'pending log ?book',
  // Signature / sign-off
  'waiting (?:on|for) (?:the )?signatures?',
  'awaiting signatures?',
  'pending signature',
  'waiting (?:on|for) sign[- ]?off',
  'awaiting sign[- ]?off',
  'sign[- ]?off pending',
  // Release / return-to-service closeout
  'awaiting (?:the )?release',
  'pending release',
  'rts paperwork',
  // State verbs applied across document types
  'work(?:ing)? (?:on|through) (?:the )?(?:paperwork|log ?book|log ?pages?)',
  'finali[sz](?:e|ing) (?:the )?(?:paperwork|log ?book|log ?pages?)',
  'complet(?:e|ing) (?:the )?(?:paperwork|log ?book|log ?pages?)',
  'finish(?:ing)? (?:up )?(?:the )?(?:paperwork|log ?book|log ?pages?)',
  'clos(?:e|ing)[- ]?(?:out )?(?:the )?(?:paperwork|log ?book|log ?pages?)',
  'wrap(?:ping)? up (?:the )?(?:paperwork|log ?book|log ?pages?)',
  'button(?:ing)? up (?:the )?(?:paperwork|log ?book|log ?pages?)',
];

/** Case-insensitive matcher built once from the anchored alternatives above. */
export const PAPERWORK_WAIT_PATTERN: RegExp = new RegExp(
  PAPERWORK_WAIT_ALTERNATIVES.join('|'),
  'i',
);

/** True when note text indicates the event is only awaiting paperwork closeout. */
export function isPaperworkWaitNote(text: string): boolean {
  return PAPERWORK_WAIT_PATTERN.test(String(text || ''));
}
